(function () {
  const dayMs = 24 * 60 * 60 * 1000;
  const instances = new Map();

  function storage() {
    return window.siteStorage || window.localStorage;
  }

  function getStorage(key) {
    try {
      return storage().get ? storage().get(key) : storage().getItem(key);
    } catch (error) {
      return null;
    }
  }

  function setStorage(key, value) {
    try {
      if (storage().set) storage().set(key, value);
      else storage().setItem(key, value);
    } catch (error) {
      return false;
    }
    return true;
  }

  function removeStorage(key) {
    try {
      if (storage().remove) storage().remove(key);
      else storage().removeItem(key);
    } catch (error) {
      return false;
    }
    return true;
  }

  function readConfig(id) {
    const script = Array.from(document.querySelectorAll("[data-subscription-popup-config]"))
      .find((element) => element.dataset.subscriptionPopupConfig === id);
    if (!script) return {};

    try {
      return JSON.parse(script.textContent || "{}");
    } catch (error) {
      console.error("Invalid subscription popup config:", error);
      return {};
    }
  }

  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function loadHcaptcha(callback) {
    if (typeof window.hcaptcha !== "undefined") {
      callback();
      return;
    }

    if (!window.hcaptchaScriptLoaded) {
      window.hcaptchaScriptLoaded = true;
      const script = document.createElement("script");
      script.src = "https://js.hcaptcha.com/1/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = () => window.dispatchEvent(new Event("hcaptchaLoaded"));
      document.head.appendChild(script);
    }

    window.addEventListener("hcaptchaLoaded", callback, { once: true });
  }

  class SubscriptionPopup {
    constructor(root) {
      this.root = root;
      this.id = root.dataset.subscriptionPopup;
      this.config = readConfig(this.id);
      this.email = root.querySelector("[data-sp-email]");
      this.form = root.querySelector("[data-sp-form]");
      this.submit = root.querySelector("[data-sp-submit]");
      this.submitIdle = root.querySelector("[data-sp-submit-idle]");
      this.submitLoading = root.querySelector("[data-sp-submit-loading]");
      this.dontShow = root.querySelector("[data-sp-dont-show]");
      this.messageWrap = root.querySelector("[data-sp-message-wrap]");
      this.messageBox = root.querySelector("[data-sp-message-box]");
      this.messageText = root.querySelector("[data-sp-message-text]");
      this.successIcon = root.querySelector("[data-sp-success-icon]");
      this.errorIcon = root.querySelector("[data-sp-error-icon]");
      this.languageToggle = root.querySelector("[data-sp-language-toggle]");
      this.languageDropdown = root.querySelector("[data-sp-language-dropdown]");
      this.languageLabel = root.querySelector("[data-sp-language-label]");
      this.languageChevron = root.querySelector("[data-sp-language-chevron]");
      this.languageInputs = Array.from(root.querySelectorAll("[data-sp-language]"));
      this.hcaptchaWidgetId = null;
      this.hcaptchaCompleted = !this.config.hcaptcha?.siteKey;
      this.isLoading = false;
      this.storageKey = `subscriptionPopup:${this.id}`;
    }

    init() {
      this.root.addEventListener("click", (event) => {
        if (event.target === this.root || event.target.closest("[data-sp-close]")) this.close();
      });
      this.root.querySelector("[data-sp-container]")?.addEventListener("click", (event) => event.stopPropagation());
      this.form?.addEventListener("submit", (event) => this.submitForm(event));
      this.email?.addEventListener("input", () => this.updateValidity());
      this.dontShow?.addEventListener("change", () => this.updateValidity());
      this.languageToggle?.addEventListener("click", () => this.toggleLanguages());
      this.languageInputs.forEach((input) => input.addEventListener("change", () => this.updateLanguageLabel()));

      document.addEventListener("click", (event) => {
        if (!this.languageDropdown || this.languageDropdown.hidden) return;
        if (!event.target.closest("[data-sp-language-toggle]") && !event.target.closest("[data-sp-language-dropdown]")) {
          this.closeLanguages();
        }
      });

      this.updateValidity();
      this.updateLanguageLabel();

      if (this.config.autoOpen && !this.hasBeenDismissed()) {
        setTimeout(() => this.open(), Number(this.config.showDelay) || 30000);
      }
    }

    selectedLanguages() {
      return this.languageInputs.filter((input) => input.checked).map((input) => input.value);
    }

    updateLanguageLabel() {
      if (!this.languageLabel) return;
      const selected = this.selectedLanguages();
      this.languageLabel.textContent = selected.length ? `Selected ${selected.join(", ").toUpperCase()}` : "Select language";
    }

    toggleLanguages() {
      if (!this.languageDropdown) return;
      const open = this.languageDropdown.hidden;
      this.languageDropdown.hidden = !open;
      this.languageChevron?.classList.toggle("sp-chevron--open", open);
    }

    closeLanguages() {
      this.languageDropdown.hidden = true;
      this.languageChevron?.classList.remove("sp-chevron--open");
    }

    updateValidity() {
      const isValid = validEmail(this.email?.value || "") && this.hcaptchaCompleted && !this.isLoading;
      if (this.submit) this.submit.disabled = !isValid;
      return isValid;
    }

    open() {
      document.body.appendChild(this.root);
      this.root.hidden = false;
      document.body.style.overflow = "hidden";
      this.email?.focus({ preventScroll: true });
      this.renderHcaptcha();
      this.updateValidity();
    }

    close() {
      this.root.hidden = true;
      document.body.style.overflow = "";
      this.closeLanguages();
      this.setDismissed(this.dontShow?.checked ? 30 * dayMs : dayMs);
    }

    hasBeenDismissed() {
      const dismissedData = getStorage(this.storageKey);
      if (!dismissedData) return false;

      try {
        const { timestamp, duration } = JSON.parse(dismissedData);
        if (Date.now() - timestamp > duration) {
          removeStorage(this.storageKey);
          return false;
        }
        return true;
      } catch (error) {
        removeStorage(this.storageKey);
        return false;
      }
    }

    setDismissed(duration) {
      if (duration) setStorage(this.storageKey, JSON.stringify({ timestamp: Date.now(), duration }));
      else removeStorage(this.storageKey);
    }

    getHcaptchaTheme() {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }

    renderHcaptcha() {
      const siteKey = this.config.hcaptcha?.siteKey;
      if (!siteKey) return;

      loadHcaptcha(() => {
        const container = document.getElementById(`popup-hcaptcha-container-${this.id}`);
        const responseField = document.getElementById(`popup-hcaptcha-response-${this.id}`);
        if (!container || typeof window.hcaptcha === "undefined" || container.hasChildNodes()) return;

        try {
          this.hcaptchaWidgetId = window.hcaptcha.render(container, {
            sitekey: siteKey,
            size: this.config.hcaptcha?.size || "compact",
            theme: this.getHcaptchaTheme(),
            callback: (token) => {
              if (responseField) responseField.value = token;
              this.hcaptchaCompleted = true;
              this.updateValidity();
            },
            "expired-callback": () => this.resetHcaptcha(),
            "error-callback": () => this.resetHcaptcha(),
          });
        } catch (error) {
          console.error("Error rendering popup hCaptcha:", error);
        }
      });
    }

    resetHcaptcha() {
      const siteKey = this.config.hcaptcha?.siteKey;
      if (!siteKey) return;

      const responseField = document.getElementById(`popup-hcaptcha-response-${this.id}`);
      if (responseField) responseField.value = "";

      try {
        if (typeof window.hcaptcha !== "undefined" && this.hcaptchaWidgetId !== null) {
          window.hcaptcha.reset(this.hcaptchaWidgetId);
        }
      } catch (error) {
        console.error("Error resetting popup hCaptcha:", error);
      }

      this.hcaptchaCompleted = false;
      this.updateValidity();
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      if (this.submitIdle) this.submitIdle.hidden = isLoading;
      if (this.submitLoading) this.submitLoading.hidden = !isLoading;
      this.updateValidity();
    }

    showMessage(message, isSuccess) {
      if (!this.messageWrap || !this.messageBox || !this.messageText) return;
      this.messageText.textContent = message;
      this.messageBox.dataset.type = isSuccess ? "success" : "error";
      if (this.successIcon) this.successIcon.hidden = !isSuccess;
      if (this.errorIcon) this.errorIcon.hidden = isSuccess;
      this.messageWrap.hidden = false;
    }

    clearMessage() {
      if (this.messageWrap) this.messageWrap.hidden = true;
      if (this.messageText) this.messageText.textContent = "";
    }

    async submitForm(event) {
      event.preventDefault();
      this.clearMessage();

      if (!validEmail(this.email?.value || "")) {
        this.showMessage(this.config.messages?.validEmail || "Please enter a valid email address", false);
        return;
      }

      const hcaptchaResponse = document.getElementById(`popup-hcaptcha-response-${this.id}`)?.value;
      if (this.config.hcaptcha?.siteKey && !hcaptchaResponse) {
        this.showMessage(this.config.messages?.captchaError || "Please complete the captcha verification", false);
        return;
      }

      this.setLoading(true);

      try {
        const payload = {
          workflows: ["change_email_list"],
          jinja_data: {
            email_address: this.email.value,
            status: "subscribed",
          },
        };
        const languages = this.selectedLanguages();
        if (languages.length) payload.jinja_data.languages = languages;

        const response = await fetch(window.location.href, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Server returned HTML instead of JSON. Check backend configuration.");
        }

        const data = await response.json();
        const message = data.result || data.message || this.config.messages?.subSuccess || "Subscription successful!";
        const isSuccess = data.success === 1 || data.success === true || data.type === "success" || (!data.result && !data.message);
        this.showMessage(message, isSuccess);

        if (isSuccess) {
          this.setDismissed(30 * dayMs);
          setTimeout(() => this.resetAfterSuccess(), 2000);
        }
      } catch (error) {
        console.error("Newsletter subscription error:", error);
        this.showMessage(error.message || this.config.messages?.errorRetry || "An error occurred. Please try again.", false);
        this.resetHcaptcha();
      } finally {
        this.setLoading(false);
      }
    }

    resetAfterSuccess() {
      this.root.hidden = true;
      document.body.style.overflow = "";
      if (this.email) this.email.value = "";
      if (this.dontShow) this.dontShow.checked = false;
      this.languageInputs.forEach((input) => {
        input.checked = false;
      });
      this.updateLanguageLabel();
      this.clearMessage();
      this.resetHcaptcha();
    }
  }

  function openPopup(id) {
    const popup = id ? instances.get(id) : instances.values().next().value;
    if (popup) popup.open();
  }

  function initSubscriptionPopups(root = document) {
    root.querySelectorAll("[data-subscription-popup]").forEach((element) => {
      const id = element.dataset.subscriptionPopup;
      if (!id || instances.has(id)) return;
      const popup = new SubscriptionPopup(element);
      instances.set(id, popup);
      popup.init();
    });
  }

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-subscription-popup-trigger]");
    if (!trigger) return;
    event.preventDefault();
    openPopup(trigger.dataset.subscriptionPopupTrigger);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      instances.forEach((popup) => {
        if (!popup.root.hidden) popup.close();
      });
      return;
    }

    const trigger = event.target.closest("[data-subscription-popup-trigger]");
    if (trigger && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      openPopup(trigger.dataset.subscriptionPopupTrigger);
    }
  });

  window.addEventListener("open-subscription-modal", (event) => openPopup(event.detail?.id));
  window.openNewsletterPopup = () => openPopup();
  window.showNewsletterModal = window.openNewsletterPopup;
  window.initSubscriptionPopups = initSubscriptionPopups;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initSubscriptionPopups());
  } else {
    initSubscriptionPopups();
  }
})();
